import { deepEq } from './canonical';
import type {
  CanonicalDetails,
  CanonicalPallet,
  CanonicalStock,
  DirtyResult,
  ProductSnapshot,
} from './types';

/*
 * Which of the three save calls actually need to run.
 *
 * The edit route saves details, stocks and pallet sizes through three separate
 * endpoints, so this is not a boolean — it decides how many network calls a
 * save costs, and whether "No changes to save" is shown instead.
 */
export const computeDirty = (
  current: {
    details: CanonicalDetails;
    stocks: CanonicalStock[];
    pallets: CanonicalPallet[];
  },
  initial: ProductSnapshot,
  /** A newly picked file. The image is not part of the canonical details. */
  hasNewImage: boolean,
  /** The saved photo was cleared (also not part of canonical details). */
  imageRemoved = false
): DirtyResult => {
  const detailsDirty = !deepEq(current.details, initial.details) || hasNewImage || imageRemoved;
  const stocksDirty = !deepEq(current.stocks, initial.stocks);

  /*
   * Switching PALLET → BULK does not need a pallet PUT: the details PATCH
   * clears them server-side. So this is only ever asked of a PALLET product.
   */
  const palletsDirty =
    current.details.deliveryType === 'PALLET' && !deepEq(current.pallets, initial.pallets);

  return {
    detailsDirty,
    stocksDirty,
    palletsDirty,
    anyDirty: detailsDirty || stocksDirty || palletsDirty,
  };
};

/**
 * The baseline to adopt after a successful save. A BULK product stores an empty
 * pallet list rather than what the form happened to be holding, matching what
 * the server now has.
 */
export const snapshotAfterSave = (current: {
  details: CanonicalDetails;
  stocks: CanonicalStock[];
  pallets: CanonicalPallet[];
}): ProductSnapshot => ({
  details: current.details,
  stocks: current.stocks,
  pallets: current.details.deliveryType === 'PALLET' ? current.pallets : [],
});
